import type { AxiosError } from 'axios';

import { useMutation } from '@northladder/services';
import {
  ErrorEventsEnum,
  errorLogToRemoteUtil,
  IApiError,
  IError,
} from '@northladder/utilities';

import { IResetPasswordWithTokenResponse } from '../types';
import { USER_ACCOUNTS_API } from '../userAccountsAPIClient';

const MUT_RESET_PASSWORD_WITH_TOKEN = 'reset-password-with-token';

interface IResetPasswordWithTokenOptions {
  onErrorCallback: (err: IApiError) => void;
  onSuccessCallback: (data: IResetPasswordWithTokenResponse) => void;
}

/**
 * Custom error item for reset password errors.
 */
interface IResetPasswordServerErrorItem {
  code?: string;
  description?: string;
  message?: string;
}

interface IResetPasswordServerErrorResponse extends IError {
  /**
   * Array of error details from the server if any.
   */
  data?: IResetPasswordServerErrorItem[];
}

export type TResetPasswordApiError = IApiError<
  AxiosError<IResetPasswordServerErrorResponse>
>;

/**
 * Formats the error message from the server to include additional details if available.
 */
const formatError = (err: TResetPasswordApiError): TResetPasswordApiError => {
  const additionalErrorInfo =
    err?.error?.response?.data?.data?.[0]?.description;
  if (!additionalErrorInfo) return err;
  return { ...err, message: additionalErrorInfo };
};

/**
 * Custom hook to reset password using a password reset token.
 */
export const useResetPasswordWithTokenMutation = ({
  onSuccessCallback,
  onErrorCallback,
}: IResetPasswordWithTokenOptions) => {
  const { mutate, data, isPending, error } = useMutation({
    mutationKey: [MUT_RESET_PASSWORD_WITH_TOKEN],
    networkMode: 'always',
    retry: false,
    mutationFn: USER_ACCOUNTS_API.resetPasswordWithToken,
    onSuccess: onSuccessCallback,
    onError: (err: TResetPasswordApiError) => {
      const formattedError = formatError(err);

      onErrorCallback(err);
      errorLogToRemoteUtil({
        error,
        errorCode: ErrorEventsEnum.ERROR_IN_API_CALL,
        errorTitle: 'useResetPasswordWithTokenMutation: Error',
        message: formattedError.message,
      });
      return formattedError;
    },
  });

  const formattedError = error ? formatError(error) : null;

  return {
    data,
    error: formattedError,
    isPending,
    resetPasswordWithToken: mutate,
  };
};
